import React, { useEffect, useState, useRef } from "react";
import { useTransactions } from "./useTransactions";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import cloneDeep from "lodash/cloneDeep";
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css'; // For responsive hiding
import '../App.css';
import '../css/FrameStyles.css';
import './TaxScenarioCalculator.css';
import { Dropdown } from "primereact/dropdown";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { fetchTaxRates } from '../utils/taxRatesCache';
import { getPortfolioValue } from '../api/portfolio';
import { executeLiquidation, optimizeLiquidation, simulateTaxImpact } from '../api/transactions';
import unlockService from '../services/unlockService';
import DashboardUnlock from './DashboardUnlock';

// Mock fetch current price for a ticker
const mockFetchCurrentPrice = async (ticker) => {
  await new Promise((res) => setTimeout(res, 400));
  return (Math.random() * 100 + 10).toFixed(2);
};

const defaultScenario = (position = {}) => ({
  id: Date.now() + Math.random(),
  ticker: position.tickerSymbol || "",
  description: position.description || "",
  action: "NONE", // default to NONE
  quantity: position.quantity || 0,
  currentPrice: position.currentPrice || 0,
  unrealizedProfitLoss: position.unrealizedProfitLoss || 0,
  costBasis: position.costBasis || 0,
  purchaseDate: position.purchaseDate || position.date || "",
  date: new Date().toISOString().slice(0, 10), // sell date
  account: position.accountName || "",
});

function TaxScenarioCalculator({ username }) {
  const { data: positions, loading } = useTransactions(
    "transactions/getCurrentPortfolio",
    username
  );
  const [scenarios, setScenarios] = useState([]);
  const [results, setResults] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [fetchingTicker, setFetchingTicker] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");
  const [expandedRows, setExpandedRows] = useState(null);
  const [resultsExpandedRows, setResultsExpandedRows] = useState(null);
  const [editingScenarioId, setEditingScenarioId] = useState(null);
  const [tempScenario, setTempScenario] = useState(null);
  const [liquidationAmount, setLiquidationAmount] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [availableTickers, setAvailableTickers] = useState([]);
  const [longTermTaxRate, setLongTermTaxRate] = useState(20);
  const [shortTermTaxRate, setShortTermTaxRate] = useState(30);
  const resultsRef = useRef(null);
  const filterControlsRef = useRef(null);
  const isMobileView = window.innerWidth <= 768;
  const [positionsVisibleCount, setPositionsVisibleCount] = useState(5);
  const [bestLotsVisibleCount, setBestLotsVisibleCount] = useState(10);
  const [otherLotsVisibleCount, setOtherLotsVisibleCount] = useState(10);
  const [resultsVisibleCount, setResultsVisibleCount] = useState(10);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [positionsTableCollapsed, setPositionsTableCollapsed] = useState(false);
  const [bestLotsTableCollapsed, setBestLotsTableCollapsed] = useState(false);
  const [otherLotsTableCollapsed, setOtherLotsTableCollapsed] = useState(false);
  const [resultsTableCollapsed, setResultsTableCollapsed] = useState(false);
  const [bestLotsExplanationExpanded, setBestLotsExplanationExpanded] = useState(false);
  const [calculatorExplanationExpanded, setCalculatorExplanationExpanded] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const [selectedBestLots, setSelectedBestLots] = useState([]);
  const [showExecuteConfirm, setShowExecuteConfirm] = useState(false);
  const [executeSummary, setExecuteSummary] = useState({ liquidationValue: 0, taxImpact: 0 });
  const [totalPortfolioValue, setTotalPortfolioValue] = useState(null);
  const [portfolioValueLoading, setPortfolioValueLoading] = useState(false);
  const [isLiquidationUnlocked, setIsLiquidationUnlocked] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);

  // Fetch current portfolio value (session-based)
  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    const fetchPortfolioValue = async () => {
      try {
        setPortfolioValueLoading(true);
        const response = await getPortfolioValue();
        if (!cancelled && response != null && typeof response.totalValue === 'number') {
          setTotalPortfolioValue(response.totalValue);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching current portfolio value:", err);
          setTotalPortfolioValue(null);
        }
      } finally {
        if (!cancelled) setPortfolioValueLoading(false);
      }
    };
    fetchPortfolioValue();
    return () => { cancelled = true; };
  }, [username]);

  // Check unlock status for Liquidation Calculator results (same dashboard unlock)
  useEffect(() => {
    const checkUnlockStatus = async () => {
      try {
        const status = await unlockService.checkUnlockStatus();
        setIsLiquidationUnlocked(status.unlocked);
      } catch (err) {
        console.error('Error checking unlock status:', err);
      }
    };
    checkUnlockStatus();
  }, []);

  const handleUnlockClick = () => setShowUnlockModal(true);
  const handleUnlockSuccess = async () => {
    setShowUnlockModal(false);
    const status = await unlockService.checkUnlockStatus();
    setIsLiquidationUnlocked(status.unlocked);
  };

  // Fetch tax rates from backend (using cached utility)
  const getTaxRates = async () => {
    if (!username) return;
    const rates = await fetchTaxRates(username);
    if (rates) {
      setLongTermTaxRate(rates.LONG_TERM);
      setShortTermTaxRate(rates.SHORT_TERM);
    }
  };

  useEffect(() => {
    if (username) {
      getTaxRates();
    }
  }, [username]);

  // Scroll to results when analysis is ready
  useEffect(() => {
    if (analysis && analysis.documentData && resultsRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [analysis]);

  // Handle scroll to show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to filter controls
  const scrollToTop = () => {
    filterControlsRef.current?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  };

  useEffect(() => {
    if (positions && positions.length) {
      // Get executed transactions from sessionStorage (for demo accounts)
      const executedTransactions = username === "demo" 
        ? JSON.parse(sessionStorage.getItem('executedTaxLossHarvesting') || '[]')
        : [];
      
      // Create a set of matching keys for executed transactions
      const executedMatchingKeys = new Set(executedTransactions.map(tx => tx.matchingKey));
      
      // Get new buy positions from sessionStorage
      const newBuyPositions = username === "demo"
        ? JSON.parse(sessionStorage.getItem('newBuyPositions') || '[]')
        : [];
      
      // Filter out executed positions by matching ticker, quantity, costBasis, and date
      const openPositions = positions.filter(
        (item) => {
          const hasSettlements = item.settlementTransactions && item.settlementTransactions.length > 0;
          if (hasSettlements && item.settlementTransactions.length > 0) {
            return false; // Skip positions with settlements
          }
          
          // Check if this position matches any executed transaction
          if (username === "demo" && executedTransactions.length > 0) {
            const itemMatchingKey = `${item.tickerSymbol}-${item.quantity}-${item.costBasis || item.price || 0}-${item.date || item.purchaseDate || ''}`;
            if (executedMatchingKeys.has(itemMatchingKey)) {
              return false; // This position was executed, exclude it
            }
          }
          
          return true;
        }
      );
      
      // Add new buy positions as if they came from backend
      const buyPositionsAsPositions = newBuyPositions.map(buy => ({
        tickerSymbol: buy.ticker,
        description: buy.description || `Buy ${buy.quantity} shares of ${buy.ticker} at $${Math.abs(buy.costBasis).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        quantity: buy.quantity,
        currentPrice: buy.costBasis, // Buy price becomes current price
        unrealizedProfitLoss: 0, // New position, no gain/loss yet
        costBasis: buy.costBasis,
        purchaseDate: buy.purchaseDate || buy.date,
        date: buy.date,
        accountName: buy.account,
        settledQuantity: 0,
        price: buy.costBasis,
        settlementTransactions: []
      }));
      
      const allPositions = [...openPositions, ...buyPositionsAsPositions];
      
      // Extract unique accounts and tickers for filtering
      console.log('Sample position data:', allPositions[0]); // Debug log
      const accounts = [...new Set(allPositions.map(pos => pos.accountName || pos.account).filter(Boolean))];
      const tickers = [...new Set(allPositions.map(pos => pos.tickerSymbol || pos.ticker).filter(Boolean))];
      
      console.log('Extracted accounts:', accounts); // Debug log
      console.log('Extracted tickers:', tickers); // Debug log
      
      const accountOptions = accounts.map(account => ({ label: account, value: account }));
      const tickerOptions = tickers.map(ticker => ({ label: ticker, value: ticker }));
      
      console.log('Account options:', accountOptions); // Debug log
      console.log('Ticker options:', tickerOptions); // Debug log
      
      setAvailableAccounts(accountOptions);
      setAvailableTickers(tickerOptions);
      
      // Update scenarios with all positions (executed ones filtered out, new buys added)
      setScenarios(
        allPositions.map((pos) =>
          defaultScenario({
            tickerSymbol: pos.tickerSymbol,
            description: pos.description || "",
            quantity: pos.quantity,
            currentPrice: Number(pos.currentPrice),
            unrealizedProfitLoss: Number(pos.unrealizedProfitLoss),
            costBasis: pos.costBasis || Number(pos.price),
            purchaseDate: pos.purchaseDate || pos.date || "",
            accountName: pos.accountName,
          })
        )
      );
    }
  }, [positions, username]);
  
  // Listen for tax loss harvesting execution events to refresh scenarios
  useEffect(() => {
    if (username === "demo") {
      const handleTaxLossHarvestingExecuted = () => {
        // Force refresh by clearing scenarios and letting the positions effect re-run
        setScenarios([]);
      };
      
      window.addEventListener('taxLossHarvestingExecuted', handleTaxLossHarvestingExecuted);
      return () => {
        window.removeEventListener('taxLossHarvestingExecuted', handleTaxLossHarvestingExecuted);
      };
    }
  }, [username]);

  const handleEditScenario = (scenario) => {
    setEditingScenarioId(scenario.id);
    setTempScenario(cloneDeep(scenario));
  };

  const handleCancelEdit = () => {
    setEditingScenarioId(null);
    setTempScenario(null);
  };

  const handleSaveEdit = async () => {
    const originalScenario = scenarios.find((s) => s.id === editingScenarioId);
    let updatedScenario = { ...tempScenario };

    if (originalScenario.ticker !== updatedScenario.ticker) {
      setFetchingTicker((prev) => ({ ...prev, [updatedScenario.id]: true }));
      const price = await mockFetchCurrentPrice(updatedScenario.ticker);
      updatedScenario.currentPrice = Number(price);
      setFetchingTicker((prev) => ({ ...prev, [updatedScenario.id]: false }));
    }
    setScenarios(
      scenarios.map((s) => (s.id === editingScenarioId ? updatedScenario : s))
    );
    setEditingScenarioId(null);
    setTempScenario(null);
  };

  const handleTempScenarioChange = (e) => {
    const { name, value } = e.target;
    setTempScenario((prev) => ({ ...prev, [name]: value }));
  };

  // Helper function to calculate capital gain tax from gain/loss for sorting
  // Defined early so it can be used in getFilteredScenarios
  const calculateCapitalGainTaxFromGainLoss = (row) => {
    const gainLoss = calculatePotentialGainLoss(row);
    if (gainLoss == null || isNaN(gainLoss) || gainLoss === 0) {
      return 0;
    }
    
    let taxTypeInfo = getTaxType(row.purchaseDate);
    if (!taxTypeInfo.type && row.termType) {
      const isLongTerm = row.termType === 'LONG' || String(row.termType).toUpperCase().includes('LONG');
      taxTypeInfo = {
        type: isLongTerm ? 'L' : 'S',
        rate: isLongTerm ? longTermTaxRate : shortTermTaxRate
      };
    }
    
    if (!taxTypeInfo.type || !taxTypeInfo.rate) {
      return 0;
    }
    
    // Calculate tax: positive for gains, negative for losses
    return gainLoss * (taxTypeInfo.rate / 100);
  };

  // Filter scenarios based on selected filters
  const getFilteredScenarios = () => {
    let filtered = [...scenarios];
    
    // Filter by accounts
    if (selectedAccounts.length > 0) {
      filtered = filtered.filter(s => selectedAccounts.includes(s.account || s.accountName));
    }
    
    // Filter by tickers
    if (selectedTickers.length > 0) {
      filtered = filtered.filter(s => selectedTickers.includes(s.ticker || s.tickerSymbol));
    }
    
    // Sort by Capital Gain Tax (low to high) by default
    filtered.sort((a, b) => {
      const taxA = calculateCapitalGainTaxFromGainLoss(a);
      const taxB = calculateCapitalGainTaxFromGainLoss(b);
      return taxA - taxB; // Low to high
    });
    
    return filtered;
  };

  // Calculate total portfolio value (sum of quantity * currentPrice for all filtered positions)
  const calculateTotalPortfolioValue = () => {
    const filtered = getFilteredScenarios();
    const total = filtered.reduce((sum, position) => {
      const quantity = position.quantity || 0;
      const currentPrice = position.currentPrice || 0;
      return sum + (quantity * currentPrice);
    }, 0);
    return total;
  };

  // Get available tickers based on selected accounts
  const getAvailableTickersForAccounts = () => {
    if (selectedAccounts.length === 0) {
      // If no accounts selected, show all tickers
      return availableTickers;
    }
    
    // Filter scenarios by selected accounts first
    const accountFilteredScenarios = scenarios.filter(s => 
      selectedAccounts.includes(s.account || s.accountName)
    );
    
    // Extract unique tickers from filtered scenarios
    const tickers = [...new Set(accountFilteredScenarios.map(s => s.ticker || s.tickerSymbol).filter(Boolean))];
    const tickerOptions = tickers.map(ticker => ({ label: ticker, value: ticker }));
    
    return tickerOptions;
  };

  const handleLiquidationCalculate = () => {
    if (liquidationAmount && liquidationAmount > 0) {
      setAmountError(false);
      handleCalculate();
    } else {
      setAmountError(true);
      setError("Please enter a target amount to liquidate before calculating.");
      setTimeout(() => setAmountError(false), 3000);
    }
  };

  const handleCalculateClick = () => {
    if (!liquidationAmount || liquidationAmount <= 0) {
      setAmountError(true);
      setError("Please enter a target amount to liquidate before calculating.");
      // Clear error state after 3 seconds
      setTimeout(() => setAmountError(false), 3000);
      return;
    }
    setAmountError(false);
    handleCalculate();
  };

  const clearFilters = () => {
    setLiquidationAmount(null);
    setSelectedAccounts([]);
    setSelectedTickers([]);
    setPositionsVisibleCount(5);
    setAmountError(false);
    setError("");
  };

  const handleExecuteSelectedLots = () => {
    if (selectedBestLots.length === 0) {
      toast.warning("Please select at least one lot to execute.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    // Calculate totals for selected lots
    const selectedLiquidationValue = selectedBestLots.reduce((sum, lot) => {
      return sum + (lot.totalAmount || 0);
    }, 0);
    
    const selectedTaxImpact = selectedBestLots.reduce((sum, lot) => {
      return sum + (lot.capitalGainTax || 0);
    }, 0);
    
    // Set summary and show confirmation dialog
    setExecuteSummary({
      liquidationValue: selectedLiquidationValue,
      taxImpact: selectedTaxImpact
    });
    setShowExecuteConfirm(true);
  };

  const confirmExecute = async () => {
    if (username !== "demo") {
      setShowExecuteConfirm(false);
      return;
    }

    try {
      // For demo accounts: Remove selected lots from positions (scenarios) for this session
      const executedCount = selectedBestLots.length;
      
      // Match selected lots to scenarios by ticker, quantity, costBasis, and purchaseDate
      // Remove executed lots from scenarios
      setScenarios(prevScenarios => {
        return prevScenarios.filter(scenario => {
          // Check if this scenario matches any of the selected lots
          return !selectedBestLots.some(lot => {
            const lotTicker = lot.symbol || lot.tickerSymbol || lot.instrument_id || '';
            const lotQuantity = Number(lot.quantity || 0);
            const lotCostBasis = Number(lot.costBasis || 0);
            const lotPurchaseDate = lot.purchaseDate || lot.dateSold || lot.date || '';
            
            const scenarioTicker = scenario.ticker || scenario.tickerSymbol || '';
            const scenarioQuantity = Number(scenario.quantity || 0);
            const scenarioCostBasis = Number(scenario.costBasis || 0);
            const scenarioPurchaseDate = scenario.purchaseDate || '';
            
            // Match if all key properties match
            return lotTicker === scenarioTicker &&
                   Math.abs(lotQuantity - scenarioQuantity) < 0.01 &&
                   Math.abs(lotCostBasis - scenarioCostBasis) < 0.01 &&
                   lotPurchaseDate === scenarioPurchaseDate;
          });
        });
      });
      
      // Prepare transactions for backend API call
      const transactionsToLog = selectedBestLots.map(lot => {
        const ticker = lot.symbol || lot.tickerSymbol || lot.instrument_id || '';
        const quantity = Number(lot.quantity || 0);
        const price = Number(lot.price || lot.currentPrice || 0);
        const costBasis = Number(lot.costBasis || 0);
        const totalAmount = quantity * price;
        const profitLoss = totalAmount - (quantity * costBasis);
        
        return {
          id: lot.id || `${ticker}-${Date.now()}`,
          type: 'SELL',
          date: lot.purchaseDate || lot.date || new Date().toISOString().slice(0, 10),
          tickerSymbol: ticker,
          quantity: quantity,
          price: price,
          currentPrice: price,
          totalAmount: totalAmount,
          costBasis: costBasis,
          profitLoss: profitLoss,
          accountName: lot.accountName || lot.account || '',
          isEquity: true
        };
      });
      
      // Call backend API to log liquidation trade execution
      try {
        const result = await executeLiquidation(transactionsToLog);

        if (result && result.status === 'success') {
          console.log('Liquidation trade execution logged:', result.message);
        } else {
          console.warn('Liquidation trade execution logging returned unexpected response:', result);
        }
      } catch (apiError) {
        console.error('Error logging liquidation trade execution to backend:', apiError);
        // Don't block execution if logging fails - still proceed with local updates
      }
      
      // Clear all filters and selections
      setSelectedBestLots([]);
      setLiquidationAmount(null);
      setSelectedAccounts([]);
      setSelectedTickers([]);
      setAnalysis(null);
      setResults([]);
      setShowExecuteConfirm(false);
      
      // Scroll back to calculator input boxes
      setTimeout(() => {
        if (filterControlsRef.current) {
          filterControlsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      // Show success toast
      toast.success(`Successfully executed ${executedCount} tax lots.`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } catch (error) {
      console.error("Error executing lots:", error);
      toast.error("Error executing lots. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const cancelExecute = () => {
    setShowExecuteConfirm(false);
  };

  // Clear selected tickers when accounts change (but only if the selected tickers are no longer available)
  useEffect(() => {
    if (selectedTickers.length > 0) {
      const availableTickers = getAvailableTickersForAccounts();
      const availableTickerValues = availableTickers.map(t => t.value);
      const validSelectedTickers = selectedTickers.filter(ticker => availableTickerValues.includes(ticker));
      
      if (validSelectedTickers.length !== selectedTickers.length) {
        setSelectedTickers(validSelectedTickers);
      }
    }
  }, [selectedAccounts]);

  // Row edit complete handler for DESKTOP
  const onRowEditComplete = async (e) => {
    let _scenarios = [...scenarios];
    const { newData, index } = e;
    if (newData.ticker !== scenarios[index].ticker) {
      setFetchingTicker((prev) => ({ ...prev, [newData.id]: true }));
      const price = await mockFetchCurrentPrice(newData.ticker);
      newData.price = Number(price);
      setFetchingTicker((prev) => ({ ...prev, [newData.id]: false }));
    }
    _scenarios[index] = { ..._scenarios[index], ...newData };
    setScenarios(_scenarios);
  };

  const handleAddScenario = () => {
    setScenarios((prev) => [...prev, defaultScenario()]);
  };

  const handleRemoveScenario = (id) => {
    setScenarios((prev) => prev.filter((s) => s.id !== id));
  };

  const handleCalculate = async () => {
    setCalculating(true);
    setError("");
    setAnalysis(null);
    setResults([]);
    setSelectedBestLots([]);
    try {
      if (liquidationAmount && liquidationAmount > 0) {
        // Tax-optimized liquidation calculation
        const filteredScenarios = getFilteredScenarios();
        const data = await optimizeLiquidation({
            username,
            liquidationAmount,
            scenarios: filteredScenarios.map((s) => ({
              id: s.id.toString(),
              instrument_id: s.ticker,
              company_name: s.ticker,
              isEquity: true,
              price: Number(s.currentPrice),
              costBasis: Number(s.costBasis),
              quantity: Number(s.quantity),
              date: s.purchaseDate || new Date().toISOString().slice(0, 10),
              state: "FILLED",
              type: "SELL",
              optionType: null,
              optionSide: null,
              optionDirection: "NONE",
              isDisallowed: false,
              profitLoss: Number(s.unrealizedProfitLoss),
              capitalGainTaxType: null,
              totalSettledQuantity: 0.0,
              settlementTransactions: [],
              accountName: s.account,
              description: s.description,
              purchaseDate: s.purchaseDate,
            }))
          });

        if (data) {
          const { optimalLiquidation, remainingTransactions, totalLiquidationValue, totalTaxImpact, fifoTaxImpact, optimizationStrategy } = data;
          
          
          // Process optimal liquidation lots
          const processedBestLots = (optimalLiquidation || []).map(tx => {
            // Use unrealizedProfitLoss from backend as potential gain/loss (this is what backend provides)
            // If unrealizedProfitLoss is not available, calculate from currentPrice, costBasis, and quantity
            let gainLoss = null;
            
            // First, try to use unrealizedProfitLoss from backend (this is the field backend provides)
            if (tx.unrealizedProfitLoss != null && tx.unrealizedProfitLoss !== undefined) {
              gainLoss = Number(tx.unrealizedProfitLoss);
            } else if (tx.profitLoss != null && tx.profitLoss !== undefined) {
              gainLoss = Number(tx.profitLoss);
            } else if (tx.gainLoss != null && tx.gainLoss !== undefined) {
              gainLoss = Number(tx.gainLoss);
            }
            
            // If unrealizedProfitLoss is not available, calculate from available fields
            if ((gainLoss == null || isNaN(gainLoss)) && tx.currentPrice != null && tx.costBasis != null && tx.quantity != null) {
              const calculated = (Number(tx.currentPrice) - Number(tx.costBasis)) * Number(tx.quantity);
              if (!isNaN(calculated)) {
                gainLoss = calculated;
              }
            }
            
            // Ensure we have a unique id for the DataTable
            // Use index in map to ensure uniqueness
            const uniqueId = tx.id || tx.instrument_id || `best-${tx.tickerSymbol || tx.symbol || 'unknown'}-${tx.date || 'nodate'}-${tx.quantity || 0}`;
            
            // Process the transaction, ensuring all required fields are present
            return {
              // Include all original fields first
              ...tx,
              // Then override with our processed values
              id: uniqueId,
              symbol: tx.tickerSymbol || tx.symbol || tx.instrument_id || tx.instrumentId || '',
              type: gainLoss != null && gainLoss >= 0 ? 'SELL (GAIN)' : 'SELL (LOSS)',
              gainLoss: gainLoss != null ? gainLoss : 0,
              profitLoss: gainLoss != null ? gainLoss : 0,
              currentPrice: tx.currentPrice != null ? Number(tx.currentPrice) : (tx.price != null ? Number(tx.price) : 0),
              quantity: tx.quantity != null ? Number(tx.quantity) : 0,
              costBasis: tx.costBasis != null ? Number(tx.costBasis) : 0,
              totalAmount: tx.totalAmount != null ? Number(tx.totalAmount) : 0,
              capitalGainTax: tx.capitalGainTax != null ? Number(tx.capitalGainTax) : 0,
              dateSold: tx.date || tx.dateSold || '',
              purchaseDate: tx.purchaseDate || tx.date || tx.dateSold || '', // Try multiple fields for purchaseDate
              termType: tx.capitalGainTaxType || tx.termType || null,
              washSale: tx.disallowed || tx.washSale || false,
              isBestLot: true
            };
          });

          // Process remaining lots
          const processedOtherLots = (remainingTransactions || []).map((tx, index) => {
            // Use unrealizedProfitLoss from backend as potential gain/loss (this is what backend provides)
            // If unrealizedProfitLoss is not available, calculate from currentPrice, costBasis, and quantity
            let gainLoss = null;
            
            // First, try to use unrealizedProfitLoss from backend (this is the field backend provides)
            if (tx.unrealizedProfitLoss != null && tx.unrealizedProfitLoss !== undefined) {
              gainLoss = Number(tx.unrealizedProfitLoss);
            } else if (tx.profitLoss != null && tx.profitLoss !== undefined) {
              gainLoss = Number(tx.profitLoss);
            } else if (tx.gainLoss != null && tx.gainLoss !== undefined) {
              gainLoss = Number(tx.gainLoss);
            }
            
            // If unrealizedProfitLoss is not available, calculate from available fields
            if ((gainLoss == null || isNaN(gainLoss)) && tx.currentPrice != null && tx.costBasis != null && tx.quantity != null) {
              const calculated = (Number(tx.currentPrice) - Number(tx.costBasis)) * Number(tx.quantity);
              if (!isNaN(calculated)) {
                gainLoss = calculated;
              }
            }
            
            // Ensure we have a unique id for the DataTable using index to guarantee uniqueness
            // Create id first, ensuring it's always unique
            const uniqueId = tx.id || tx.instrument_id || `other-${index}-${tx.tickerSymbol || tx.symbol || 'unknown'}-${tx.date || 'nodate'}-${tx.quantity || 0}-${Math.random().toString(36).substr(2, 9)}`;
            
            // Process the transaction, ensuring all required fields are present
            // Don't spread tx at the end to avoid overwriting our processed fields
            return {
              // Include all original fields first
              ...tx,
              // Then override with our processed values
              id: uniqueId,
              symbol: tx.tickerSymbol || tx.symbol || tx.instrument_id || tx.instrumentId || '',
              type: gainLoss != null && gainLoss >= 0 ? 'SELL (GAIN)' : 'SELL (LOSS)',
              gainLoss: gainLoss != null ? gainLoss : 0,
              profitLoss: gainLoss != null ? gainLoss : 0,
              currentPrice: tx.currentPrice != null ? Number(tx.currentPrice) : (tx.price != null ? Number(tx.price) : 0),
              quantity: tx.quantity != null ? Number(tx.quantity) : 0,
              costBasis: tx.costBasis != null ? Number(tx.costBasis) : 0,
              totalAmount: tx.totalAmount != null ? Number(tx.totalAmount) : 0,
              capitalGainTax: tx.capitalGainTax != null ? Number(tx.capitalGainTax) : 0,
              dateSold: tx.date || tx.dateSold || '',
              purchaseDate: tx.purchaseDate || tx.date || tx.dateSold || '',
              termType: tx.capitalGainTaxType || tx.termType || null,
              washSale: tx.disallowed || tx.washSale || false,
              isBestLot: false
            };
          });

          // Create documentData structure
          data.documentData = {
            bestTaxLots: processedBestLots,
            otherLots: processedOtherLots,
            totalTaxSavings: totalTaxImpact,
            totalTaxImpact: totalTaxImpact,
            fifoTaxImpact: fifoTaxImpact,
            totalLiquidationValue,
            optimizationStrategy,
            accountName: 'Portfolio'
          };
          
        }

        setAnalysis(data);
        // Reset visible counts for results tables
        setBestLotsVisibleCount(10);
        setOtherLotsVisibleCount(10);
      } else {
        // Regular calculation without liquidation amount - calculate tax impact for all filtered positions
        const filteredScenarios = getFilteredScenarios();
      const transactions = filteredScenarios.map((s) => ({
        id: s.id.toString(),
        instrument_id: s.ticker,
          company_name: s.ticker,
        isEquity: true,
          price: Number(s.currentPrice),
        costBasis: Number(s.costBasis),
        quantity: Number(s.quantity),
          date: s.date || new Date().toISOString().slice(0, 10),
        state: "FILLED",
          type: "SELL",
        optionType: null,
        optionSide: null,
        optionDirection: "NONE",
        isDisallowed: false,
          profitLoss: calculatePotentialGainLoss(s),
        capitalGainTaxType: null,
        totalSettledQuantity: 0.0,
        settlementTransactions: [],
        accountName: s.account,
        description: s.description,
        purchaseDate: s.purchaseDate,
      }));
        
      const data = await simulateTaxImpact({ username, transactions });

      if (data.documentData) {
        const { shortTermGains, longTermGains, shortTermLosses, longTermLosses, accountNumber, brokerName } = data.documentData;
        const account = accountNumber || brokerName;
          
        data.documentData.transactions = [
          ...(shortTermGains || []),
          ...(longTermGains || []),
          ...(shortTermLosses || []),
          ...(longTermLosses || []),
        ].map(tx => ({ 
          ...tx, 
          type: tx.gainLoss >= 0 ? 'SELL (GAIN)' : 'SELL (LOSS)',
            accountName: account,
            purchaseDate: tx.purchaseDate || tx.date || tx.dateSold, // Ensure purchaseDate is set
            dateSold: tx.dateSold || tx.date || tx.purchaseDate // Ensure dateSold is set
        }));
      }

      setAnalysis(data);
      // Reset visible count for results table
      setResultsVisibleCount(10);
      }
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "An error occurred during tax simulation."
      );
    } finally {
      setCalculating(false);
    }
  };


  // Helper function to determine if position is short-term or long-term
  const getTaxType = (purchaseDate) => {
    if (!purchaseDate) return { type: null, rate: null, label: null };
    
    try {
      const purchase = new Date(purchaseDate);
      const now = new Date();
      const diffTime = Math.abs(now - purchase);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays > 365) {
        return {
          type: 'L',
          rate: longTermTaxRate,
          label: `Long Term at ${longTermTaxRate.toFixed(2)}% tax rate`
        };
      } else {
        return {
          type: 'S',
          rate: shortTermTaxRate,
          label: `Short Term at ${shortTermTaxRate.toFixed(2)}% tax rate`
        };
      }
    } catch (err) {
      console.error('Error calculating tax type:', err);
      return { type: null, rate: null, label: null };
    }
  };

  // Helper function to format currency with commas
  const formatCurrencyValue = (amount) => {
    const numeric = Number(amount);
    if (isNaN(numeric)) return '$0.00';
    const formatted = Math.abs(numeric).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return numeric < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  // Helper function to format number with commas (for quantity, non-currency values)
  const formatNumberWithCommas = (value, decimals = 2) => {
    const numeric = Number(value);
    if (isNaN(numeric)) return '0.00';
    return numeric.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Helper function to format price with commas (currency format)
  const formatPrice = (value) => {
    const numeric = Number(value);
    if (isNaN(numeric)) return '$0.00';
    return formatCurrencyValue(numeric);
  };

  const calculatePotentialGainLoss = (row) => {
    // For results tables, prioritize gainLoss (which should be set from backend's unrealizedProfitLoss)
    // Note: 0 is a valid value (break-even), so we check for null/undefined specifically
    if (row.gainLoss != null && row.gainLoss !== undefined) {
      const val = Number(row.gainLoss);
      if (!isNaN(val)) {
        return val; // Return even if 0, as that's a valid break-even value
      }
    }
    
    // Prioritize unrealizedProfitLoss from backend (this is the field backend provides)
    if (row.unrealizedProfitLoss != null && row.unrealizedProfitLoss !== undefined) {
      const val = Number(row.unrealizedProfitLoss);
      if (!isNaN(val)) {
        return val; // Return even if 0
      }
    }
    
    // Fallback to profitLoss if available
    if (row.profitLoss != null && row.profitLoss !== undefined) {
      const val = Number(row.profitLoss);
      if (!isNaN(val)) {
        return val; // Return even if 0
      }
    }
    
    // Calculate based on current price, cost basis, and quantity
      if (row.currentPrice != null && row.costBasis != null && row.quantity != null) {
      const currentPrice = Number(row.currentPrice);
      const costBasis = Number(row.costBasis);
      const quantity = Number(row.quantity);
      
      if (!isNaN(currentPrice) && !isNaN(costBasis) && !isNaN(quantity)) {
        const value = (currentPrice - costBasis) * quantity;
        if (!isNaN(value)) {
          return Number(value); // Return even if 0
        }
      }
    }
    
    // Last resort: check gainLossValue if available
    if (row.gainLossValue != null && row.gainLossValue !== undefined) {
      const val = Number(row.gainLossValue);
      if (!isNaN(val)) {
        return val; // Return even if 0
      }
    }
    
    return null; // Return null only if truly unavailable
  };

  const renderPotentialGainLoss = (row) => {
    const value = calculatePotentialGainLoss(row);

    // Check if value is valid - only return N/A if value is truly unavailable (null or undefined)
    // Note: value of 0 is valid (break-even)
    if (value == null || value === undefined || isNaN(value)) {
      return <span style={{ color: '#888' }}>N/A</span>;
    }
    
    // Try to get tax type - prioritize termType from backend, then purchaseDate, then dateSold
    let taxTypeInfo = null;
    
    // First, check if we have termType from backend (most reliable)
    if (row.termType) {
      const termTypeUpper = String(row.termType).toUpperCase();
      const isLongTerm = termTypeUpper === 'LONG' || termTypeUpper.includes('LONG');
      taxTypeInfo = {
        type: isLongTerm ? 'L' : 'S',
        rate: isLongTerm ? longTermTaxRate : shortTermTaxRate,
        label: isLongTerm 
          ? `Long Term at ${longTermTaxRate.toFixed(2)}% tax rate`
          : `Short Term at ${shortTermTaxRate.toFixed(2)}% tax rate`
      };
    }
    
    // If no termType, try purchaseDate
    if (!taxTypeInfo && row.purchaseDate) {
      taxTypeInfo = getTaxType(row.purchaseDate);
    }
    
    // If still no tax type, try dateSold (which might be purchase date in some responses)
    if (!taxTypeInfo && row.dateSold) {
      taxTypeInfo = getTaxType(row.dateSold);
    }
    
    // If we still don't have tax type, show value without S/L badge
    if (!taxTypeInfo || !taxTypeInfo.type) {
      const displayValue = formatCurrencyValue(value);
    const color = value > 0 ? '#009688' : value < 0 ? '#d9534f' : '#888';
      return <span style={{ color, fontWeight: 600 }}>{displayValue}</span>;
    }
    
    // Show value with S/L badge
    const displayValue = formatCurrencyValue(value);
    const color = value > 0 ? '#009688' : value < 0 ? '#d9534f' : '#888';
    
    return (
      <span style={{ color, fontWeight: 600 }}>
        <span className="table-value-with-annotation">
          {displayValue}
          <span className="table-annotation">
            (
            <span
              className="table-tooltip-trigger tax-type-tooltip"
              data-pr-tooltip={taxTypeInfo.label}
              title={taxTypeInfo.label}
            >
              {taxTypeInfo.type}
            </span>
            )
          </span>
        </span>
      </span>
    );
  };

  const renderCapitalGainTax = (row) => {
    // For results tables, use the capitalGainTax from backend if available
    // Note: Backend should return negative values for losses (tax savings/offset)
    if (row.capitalGainTax != null && row.capitalGainTax !== 0) {
      const taxAmount = Number(row.capitalGainTax);
      const displayValue = formatCurrencyValue(taxAmount);
      
      // Try to get tax type info for the badge
      let taxTypeInfo = getTaxType(row.purchaseDate);
      if (!taxTypeInfo.type && row.termType) {
        const isLongTerm = row.termType === 'LONG' || row.termType?.toUpperCase().includes('LONG');
        taxTypeInfo = {
          type: isLongTerm ? 'L' : 'S',
          rate: isLongTerm ? longTermTaxRate : shortTermTaxRate,
          label: isLongTerm 
            ? `Long Term at ${longTermTaxRate.toFixed(2)}% tax rate`
            : `Short Term at ${shortTermTaxRate.toFixed(2)}% tax rate`
        };
      }
      if (!taxTypeInfo.type && row.dateSold) {
        taxTypeInfo = getTaxType(row.dateSold);
      }
      
      // Determine color based on whether it's a tax (positive) or tax savings (negative)
      const color = taxAmount > 0 ? '#009688' : taxAmount < 0 ? '#d9534f' : '#888';
      
      if (taxTypeInfo.type) {
        return (
          <span style={{ fontWeight: 600, color }}>
            <span className="table-value-with-annotation">
              {displayValue}
              <span className="table-annotation">
                (
                <span
                  className="table-tooltip-trigger tax-type-tooltip"
                  data-pr-tooltip={taxTypeInfo.label}
                  title={taxTypeInfo.label}
                >
                  {taxTypeInfo.type}
                </span>
                )
              </span>
            </span>
          </span>
        );
      } else {
        return <span style={{ fontWeight: 600, color }}>{displayValue}</span>;
      }
    }
    
    // Fallback: calculate tax from gain/loss (for positions table)
    const gainLoss = calculatePotentialGainLoss(row);
    let taxTypeInfo = getTaxType(row.purchaseDate);
    
    // If we have termType from backend but no purchaseDate, use termType
    if (!taxTypeInfo.type && row.termType) {
      const isLongTerm = row.termType === 'LONG' || row.termType?.toUpperCase().includes('LONG');
      taxTypeInfo = {
        type: isLongTerm ? 'L' : 'S',
        rate: isLongTerm ? longTermTaxRate : shortTermTaxRate,
        label: isLongTerm 
          ? `Long Term at ${longTermTaxRate.toFixed(2)}% tax rate`
          : `Short Term at ${shortTermTaxRate.toFixed(2)}% tax rate`
      };
    }
    
    if (!taxTypeInfo.type || gainLoss === 0 || isNaN(gainLoss)) {
      return <span style={{ color: '#888' }}>$0.00</span>;
    }
    
    // Calculate tax: positive for gains, negative for losses (tax savings)
    let taxAmount = 0;
    if (gainLoss > 0) {
      // Tax on gains
      taxAmount = gainLoss * (taxTypeInfo.rate / 100);
    } else if (gainLoss < 0) {
      // Tax savings/offset for losses (negative value)
      taxAmount = gainLoss * (taxTypeInfo.rate / 100);
    }
    
    const displayValue = formatCurrencyValue(taxAmount);
    const color = taxAmount > 0 ? '#009688' : taxAmount < 0 ? '#d9534f' : '#888';
    
    return (
      <span style={{ fontWeight: 600, color }}>
        <span className="table-value-with-annotation">
          {displayValue}
          <span className="table-annotation">
            (
            <span
              className="table-tooltip-trigger tax-type-tooltip"
              data-pr-tooltip={taxTypeInfo.label}
              title={taxTypeInfo.label}
            >
              {taxTypeInfo.type}
            </span>
            )
          </span>
        </span>
      </span>
    );
  };

  // Helper functions for totals
  const calculateTotalLiquidationValue = (data) => {
    return data.reduce((total, item) => total + (Number(item.totalAmount) || 0), 0);
  };

  const calculateTotalCapitalGainTax = (data) => {
    return data.reduce((total, item) => total + (Number(item.capitalGainTax) || 0), 0);
  };

  const renderTotalsRow = (data, label, fifoTaxImpact = null, profitreeTaxImpact = null) => {
    const totalLiquidation = calculateTotalLiquidationValue(data);
    const totalTax = calculateTotalCapitalGainTax(data);
    
    // Use provided tax impacts if available (for Best Tax Lots), otherwise use calculated totalTax
    const displayTax = profitreeTaxImpact != null ? profitreeTaxImpact : totalTax;
    const savings = fifoTaxImpact != null && profitreeTaxImpact != null 
      ? (fifoTaxImpact - profitreeTaxImpact) 
      : null;
    
    return (
      <div className="totals-row">
        <div className="totals-content">
          <div className="totals-label">{label} Totals:</div>
          <div className="totals-values">
            <span className="total-item">
              <strong>Liquidation Value: {formatCurrencyValue(totalLiquidation)}</strong>
            </span>
            {fifoTaxImpact != null && profitreeTaxImpact != null ? (
              <>
                <span className="total-item">
                  <strong>FIFO Tax: {formatCurrencyValue(fifoTaxImpact)}</strong>
                </span>
                <span className="total-item">
                  <strong>ProfiTree Tax: <span className="profitree-tax-highlight">{formatCurrencyValue(displayTax)}</span></strong>
                </span>
                <span className="total-item savings-highlight">
                  <strong>You Save: <span className="savings-amount">{formatCurrencyValue(savings)}</span></strong>
                </span>
              </>
            ) : (
              <span className="total-item">
                <strong>Capital Gain Tax: {formatCurrencyValue(displayTax)}</strong>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const allowExpansion = (rowData) => {
    return isMobileView;
  };

  const rowExpansionTemplate = (data) => {
    if (isMobileView && editingScenarioId === data.id) {
      return (
        <div className="mobile-expand-info editable">
          <div className="expand-row">
            <h4>Description:</h4>
            <input type="text" name="description" value={tempScenario.description} onChange={handleTempScenarioChange} className="p-inputtext p-component" />
          </div>
          <div className="expand-row">
            <h4>Quantity:</h4>
            <input type="number" name="quantity" value={tempScenario.quantity} onChange={handleTempScenarioChange} className="p-inputtext p-component" />
          </div>
          <div className="expand-row">
            <h4>Current Price:</h4>
            <input type="number" name="currentPrice" value={tempScenario.currentPrice} onChange={handleTempScenarioChange} className="p-inputtext p-component" />
          </div>
          <div className="expand-row">
            <h4>Cost Basis:</h4>
            <input type="number" name="costBasis" value={tempScenario.costBasis} onChange={handleTempScenarioChange} className="p-inputtext p-component" />
          </div>
          <div className="expand-row">
            <h4>Purchase Date:</h4>
            <input type="date" name="purchaseDate" value={tempScenario.purchaseDate} onChange={handleTempScenarioChange} className="p-inputtext p-component" />
          </div>
          <div className="expand-row">
            <h4>Account:</h4>
            <input type="text" name="account" value={tempScenario.account} onChange={handleTempScenarioChange} className="p-inputtext p-component" />
          </div>
          <div className="expand-row action-buttons">
            <Button label="Save" icon="pi pi-check" onClick={handleSaveEdit} className="p-button-success" />
            <Button label="Cancel" icon="pi pi-times" onClick={handleCancelEdit} className="p-button-secondary" />
          </div>
        </div>
      );
    }
    return (
      <div className="mobile-expand-info">
        <div className="expand-row">
          <h4>Description:</h4>
          <p>{data.description || 'N/A'}</p>
        </div>
        <div className="expand-row">
          <h4>Quantity:</h4>
          <p>{data.quantity != null ? formatNumberWithCommas(data.quantity, 2) : '0.00'}</p>
        </div>
        <div className="expand-row">
          <h4>Current Price:</h4>
          <p>{data.currentPrice != null ? formatPrice(data.currentPrice) : '$0.00'}</p>
        </div>
        <div className="expand-row">
          <h4>Cost Basis:</h4>
          <p>{data.costBasis != null ? formatPrice(data.costBasis) : '$0.00'}</p>
        </div>
        <div className="expand-row">
          <h4>Purchase Date:</h4>
          <p>{data.purchaseDate || 'N/A'}</p>
        </div>
        <div className="expand-row">
          <h4>Account:</h4>
          <p>{data.account || 'N/A'}</p>
        </div>
        {!isMobileView && (
          <>
            <div className="expand-row">
              <h4>Potential Gain/Loss:</h4>
              <div>{renderPotentialGainLoss(data)}</div>
            </div>
            <div className="expand-row">
              <h4>Capital Gain Tax:</h4>
              <div>{renderCapitalGainTax(data)}</div>
            </div>
            <div className="expand-row">
              <h4>Liquidation Value:</h4>
              <p>{data.totalAmount ? formatCurrencyValue(data.totalAmount) : 'N/A'}</p>
            </div>
          </>
        )}
      </div>
    );
  };

  const resultsRowExpansionTemplate = (data) => {
    // Use the renderCapitalGainTax function to get properly formatted tax
    const capitalGainTaxValue = data.capitalGainTax != null ? Number(data.capitalGainTax) : 0;
    
    // If capitalGainTax is not available, calculate it
    let displayTax = capitalGainTaxValue;
    if (capitalGainTaxValue === 0 || isNaN(capitalGainTaxValue)) {
      const potentialGainLoss = calculatePotentialGainLoss(data);
      if (potentialGainLoss != null && !isNaN(potentialGainLoss)) {
        const taxTypeInfo = getTaxType(data.purchaseDate || data.dateSold);
        if (taxTypeInfo.type && taxTypeInfo.rate) {
          displayTax = potentialGainLoss * (taxTypeInfo.rate / 100);
        }
      }
    }

    return (
      <div className="mobile-expand-info">
        <div className="expand-row">
          <h4>Action:</h4>
          <p>{data.type || 'N/A'}</p>
        </div>
        <div className="expand-row">
          <h4>Quantity:</h4>
          <p>{data.quantity != null ? formatNumberWithCommas(data.quantity, 2) : 'N/A'}</p>
        </div>
        <div className="expand-row">
          <h4>Current Price:</h4>
          <p>{data.currentPrice != null ? formatPrice(data.currentPrice) : 'N/A'}</p>
        </div>
        <div className="expand-row">
          <h4>Cost Basis:</h4>
          <p>{data.costBasis != null ? formatPrice(data.costBasis) : 'N/A'}</p>
        </div>
        <div className="expand-row">
          <h4>Date Sold:</h4>
          <p>{data.dateSold || 'N/A'}</p>
        </div>
        <div className="expand-row">
          <h4>Potential Gain/Loss:</h4>
          <div>{renderPotentialGainLoss(data)}</div>
        </div>
        <div className="expand-row">
          <h4>Capital Gain Tax:</h4>
          <div>{renderCapitalGainTax(data)}</div>
        </div>
        <div className="expand-row">
          <h4>Liquidation Value:</h4>
          <p>{data.totalAmount != null ? formatCurrencyValue(data.totalAmount) : 'N/A'}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="tax-scenario-calculator-container">
      {error && (
        <div className="error-message">{error}</div>
      )}
      
      {/* Filter Controls */}
      <div className="filter-controls" ref={filterControlsRef}>
        <div className="calculator-header-section">
          <div className="calculator-title-and-portfolio">
            <h3>Quick Liquidation Calculator</h3>
            <div className="quick-calc-portfolio-value">
              {portfolioValueLoading ? (
                <span className="quick-calc-portfolio-loading">Loading...</span>
              ) : totalPortfolioValue != null ? (
                <>
                  <span className="quick-calc-portfolio-label">Total portfolio value: </span>
                  <span className="quick-calc-portfolio-amount">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalPortfolioValue)}
                  </span>
                </>
              ) : null}
            </div>
          </div>
          <div className="calculator-explanation-top-right">
            <div 
              className="explanation-header"
              onClick={() => setCalculatorExplanationExpanded(!calculatorExplanationExpanded)}
            >
              <span className="info-icon">ℹ️</span>
              <span className="explanation-title">Why use this calculator?</span>
              <span className="explanation-toggle">
                {calculatorExplanationExpanded ? '−' : '+'}
              </span>
            </div>
            {calculatorExplanationExpanded && (
              <div className="explanation-content explanation-content-dropdown">
                <p>
                  The main use of the calculator is to access cash from your <strong>stock market investments tax-efficiently</strong>. 
                  Common reasons to use it include:
                </p>
                <ul className="explanation-list">
                  <li>Need to <strong>reduce tax liability</strong> for the year</li>
                  <li>Need to sell for a <strong>home purchase or emergencies</strong></li>
                  <li><strong>Reallocation of assets</strong></li>
                  <li><strong>Cash out of a hot market</strong></li>
                  <li>And many more scenarios where tax efficiency matters</li>
                </ul>
              </div>
            )}
          </div>
        </div>
        
        <div className="filter-row">
          <div className="filter-item filter-item-highlighted">
            <label className="filter-label-highlighted">Target Amount to Liquidate ($)</label>
            <InputNumber
              value={liquidationAmount}
              onValueChange={(e) => {
                setLiquidationAmount(e.value);
                setAmountError(false);
                setError("");
              }}
              mode="currency"
              currency="USD"
              locale="en-US"
              placeholder="Enter amount (e.g., $10,000)"
              className={amountError ? "input-error" : ""}
            />
          </div>
          
          <div className="filter-item">
            <label>Filter by Accounts</label>
            <MultiSelect
              value={selectedAccounts}
              options={availableAccounts}
              onChange={(e) => setSelectedAccounts(e.value)}
              placeholder="Search accounts..."
              display="chip"
              filter
              showClear
              maxSelectedLabels={2}
              selectedItemsLabel="{0} accounts selected"
              panelStyle={{ width: '350px' }}
            />
          </div>
          
          <div className="filter-item">
            <label>Filter by Stocks</label>
            <MultiSelect
              value={selectedTickers}
              options={selectedAccounts.length > 0 ? getAvailableTickersForAccounts() : availableTickers}
              onChange={(e) => {
                setSelectedTickers(e.value);
              }}
              placeholder={selectedAccounts.length > 0 ? "Search stocks in selected accounts..." : "Search stocks..."}
              display="chip"
              filter
              showClear
              maxSelectedLabels={0}
              selectedItemsLabel=""
              panelStyle={{ width: '300px' }}
              valueTemplate={() => {
                if (selectedTickers.length === 0) {
                  return <span style={{color: '#6c757d', fontStyle: 'italic'}}>
                    {selectedAccounts.length > 0 ? "Search stocks in selected accounts..." : "Search stocks..."}
                  </span>;
                }
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                    {selectedTickers.map(ticker => (
                      <span 
                        key={ticker} 
                        style={{
                          background: '#f8f9fa',
                          border: '1px solid #e9ecef',
                          borderRadius: '16px',
                          padding: '4px 10px',
                          fontSize: '0.8rem',
                          color: '#264653',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'default'
                        }}
                      >
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#2a9d8f',
                          flexShrink: 0,
                          border: '1px solid rgba(42, 157, 143, 0.3)'
                        }}></span>
                        <span>{ticker}</span>
                        <span 
                          style={{
                            cursor: 'pointer', 
                            color: '#dc3545', 
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            lineHeight: '1',
                            marginLeft: '2px',
                            padding: '0 2px',
                            transition: 'color 0.2s ease'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTickers(selectedTickers.filter(t => t !== ticker));
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#a71e2a';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#dc3545';
                          }}
                        >
                          ×
                        </span>
                      </span>
                    ))}
                  </div>
                );
              }}
              itemTemplate={(option) => {
                if (!option || !option.label) return null;
                return (
                  <div className="stock-option">
                    <span className="stock-color-dot" style={{ background: '#2a9d8f' }}></span>
                    <span className="stock-label">{option.label}</span>
                  </div>
                );
              }}
              selectedItemTemplate={(option) => {
                if (!option || !option.label) return null;
                return (
                  <span className="stock-chip">
                    <span className="stock-color-dot-small" style={{ background: '#2a9d8f' }}></span>
                    <span>{option.label}</span>
                  </span>
                );
              }}
            />
          </div>
        </div>
        
        <div className="filter-actions">
          <div className="calculator-instruction">
            <p>Start by entering your liquidation amount and applying filters above</p>
          </div>
          <div className="button-group">
            <Button
              label="Calculate Best Tax Lots"
              icon="pi pi-calculator"
              onClick={liquidationAmount && liquidationAmount > 0 ? handleLiquidationCalculate : handleCalculateClick}
              className="p-button-success calculate-button-primary"
            />
            <Button
              label="Clear Filters"
              icon="pi pi-refresh"
              onClick={clearFilters}
              className="p-button-secondary"
              outlined
            />
            <span className="positions-count">
              {getFilteredScenarios().length} positions available
            </span>
          </div>
        </div>
      </div>
      
      <div className="scenario-table-wrapper">
        <div className="table-header-controls">
          <h4 className="table-title">
            Positions (<span className="portfolio-total-value">{formatCurrencyValue(calculateTotalPortfolioValue())}</span>)
          </h4>
          <Button
            icon={positionsTableCollapsed ? "pi pi-chevron-down" : "pi pi-chevron-up"}
            onClick={() => {
              setPositionsTableCollapsed(!positionsTableCollapsed);
              if (positionsTableCollapsed) {
                setPositionsVisibleCount(5);
              }
            }}
            className="p-button-text p-button-sm collapse-button"
            label={positionsTableCollapsed ? "Expand" : "Collapse"}
          />
        </div>
        {!positionsTableCollapsed && (
          <>
            <DataTable
              value={getFilteredScenarios().slice(0, positionsVisibleCount)}
              responsiveLayout="stack"
              emptyMessage="No scenarios match your filters. Try adjusting your filters or add a new scenario!"
              editMode="row"
              dataKey="id"
              onRowEditComplete={onRowEditComplete}
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
            >
          <Column
            expander={allowExpansion}
            style={{ width: "1rem" }}
            className={isMobileView ? "mobile-row-expander" : ""}
          />
          <Column field="ticker" header="Ticker" editor={(options) => (
            <input
              value={options.value}
              onChange={e => options.editorCallback(e.target.value)}
              style={{ minWidth: 80 }}
              placeholder="Ticker"
              disabled={fetchingTicker[options.rowData.id]}
            />
          )} />
          {isMobileView ? (
            <Column field="description" header="Description" />
          ) : (
            <Column field="description" header="Description" editor={(options) => (
              <input value={options.value} onChange={e => options.editorCallback(e.target.value)} placeholder="Description" />
            )} />
          )}
          {!isMobileView && (
            <Column 
              field="quantity" 
              header="Quantity" 
              body={(rowData) => rowData.quantity != null ? formatNumberWithCommas(rowData.quantity, 2) : '0.00'}
              editor={(options) => (
                <input type="number" min="1" value={options.value} onChange={e => options.editorCallback(e.target.value)} />
              )} 
            />
          )}
          {!isMobileView && (
            <Column 
              field="currentPrice" 
              header="Current Price" 
              body={(rowData) => rowData.currentPrice != null ? formatPrice(rowData.currentPrice) : '$0.00'}
              editor={(options) => (
                <input type="number" min="0" step="0.01" value={options.value} onChange={e => options.editorCallback(e.target.value)} />
              )} 
            />
          )}
          {!isMobileView && (
            <Column 
              field="costBasis" 
              header="Cost Basis" 
              body={(rowData) => rowData.costBasis != null ? formatPrice(rowData.costBasis) : '$0.00'}
              editor={(options) => (
                <input type="number" min="0" step="0.01" value={options.value} onChange={e => options.editorCallback(e.target.value)} />
              )} 
            />
          )}
          {!isMobileView && (
            <Column field="purchaseDate" header="Purchase Date" editor={(options) => (
              <input type="date" value={options.value} onChange={e => options.editorCallback(e.target.value)} />
            )} />
          )}
          {/* <Column
            field="potentialGainLoss"
            header="Potential Gain/Loss"
            body={renderPotentialGainLoss}
            style={{ minWidth: 180 }}
          /> */}
          {/* {!isMobileView && (
            <Column
              field="capitalGainTax"
              header="Capital Gain Tax"
              body={renderCapitalGainTax}
              style={{ minWidth: 160 }}
              sortable
              sortFunction={(e) => {
                // Custom sort function to sort by actual tax value (low to high)
                // Add null/undefined checks
                if (!e.data1 && !e.data2) return 0;
                if (!e.data1) return 1;
                if (!e.data2) return -1;
                
                // Calculate tax for both rows since positions table doesn't have capitalGainTax field
                const calcTax1 = calculateCapitalGainTaxFromGainLoss(e.data1);
                const calcTax2 = calculateCapitalGainTaxFromGainLoss(e.data2);
                
                return calcTax1 - calcTax2; // Low to high
              }}
            />
          )} */}
        </DataTable>
        <div className="show-more-container">
          {getFilteredScenarios().length > positionsVisibleCount ? (
            <Button
              label={`Show More (${getFilteredScenarios().length - positionsVisibleCount} remaining)`}
              icon="pi pi-chevron-down"
              onClick={() => setPositionsVisibleCount(prev => Math.min(prev + 10, getFilteredScenarios().length))}
              className="p-button-outlined p-button-secondary show-more-button"
            />
          ) : positionsVisibleCount > 5 ? (
            <Button
              label="Show Less"
              icon="pi pi-chevron-up"
              onClick={() => setPositionsVisibleCount(5)}
              className="p-button-outlined p-button-secondary show-more-button"
            />
          ) : null}
        </div>
        </>
        )}
      </div>
      <Tooltip target=".tax-type-tooltip" />
      {calculating && (
        <div className="loading-message">Calculating tax impact...</div>
      )}
      {analysis && analysis.documentData && (
        <div style={{ position: 'relative' }}>
          {!isLiquidationUnlocked && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '20px',
              marginBottom: '20px',
              zIndex: 10,
              position: 'relative'
            }}>
              <Button
                type="button"
                onClick={handleUnlockClick}
                className="p-button-success"
                style={{
                  background: 'linear-gradient(135deg, #2A9D8F 0%, #264653 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '700',
                  boxShadow: '0 4px 15px rgba(42, 157, 143, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FontAwesomeIcon icon={faLock} />
                Unlock Details
              </Button>
            </div>
          )}
          <div
            className="results-table-wrapper"
            ref={resultsRef}
            style={{
              filter: !isLiquidationUnlocked ? 'blur(8px)' : 'none',
              pointerEvents: !isLiquidationUnlocked ? 'none' : 'auto'
            }}
          >
          {liquidationAmount && liquidationAmount > 0 && analysis.documentData.bestTaxLots ? (
            // Tax-optimized results with two sections
            <>
              <h3>Tax-Optimized Liquidation Results</h3>

              {/* Best Tax Lots Section */}
              <div className="results-section best-tax-lots-highlight">
                <div className="table-header-controls">
                  <h3 className="section-header">
                   Best Tax Lots ({analysis.documentData.bestTaxLots?.length || 0} lots)
                  </h3>
                  <Button
                    icon={bestLotsTableCollapsed ? "pi pi-chevron-down" : "pi pi-chevron-up"}
                    onClick={() => {
                      setBestLotsTableCollapsed(!bestLotsTableCollapsed);
                      if (bestLotsTableCollapsed) {
                        setBestLotsVisibleCount(10);
                      }
                    }}
                    className="p-button-text p-button-sm collapse-button"
                    label={bestLotsTableCollapsed ? "Expand" : "Collapse"}
                  />
                </div>
                <div className="section-description-with-explanation">
                  <p className="section-description">
                    Optimized lots selected to minimize taxes. Prioritizes losses and long-term gains.
                  </p>
                  <div className="best-lots-explanation-inline-right">
                    <div 
                      className="explanation-header"
                      onClick={() => setBestLotsExplanationExpanded(!bestLotsExplanationExpanded)}
                    >
                      <span className="info-icon">ℹ️</span>
                      <span className="explanation-title">How are Best Tax Lots selected?</span>
                      <span className="explanation-toggle">
                        {bestLotsExplanationExpanded ? '−' : '+'}
                      </span>
                    </div>
                    {bestLotsExplanationExpanded && (
                      <div className="explanation-content explanation-content-dropdown">
                        <p>
                          The algorithm selects positions that create the <strong>lowest taxes first</strong>, such as those with <strong>losses or lower gains</strong>, until it reaches your <strong>target amount</strong>.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {!bestLotsTableCollapsed && (
                  <>
                    <DataTable 
                      value={(analysis.documentData.bestTaxLots || []).slice(0, bestLotsVisibleCount)} 
                      responsiveLayout="stack"
                      expandedRows={resultsExpandedRows}
                      onRowToggle={(e) => setResultsExpandedRows(e.data)}
                      rowExpansionTemplate={resultsRowExpansionTemplate}
                      sortField="totalAmount"
                      sortOrder={-1}
                      defaultSortOrder={-1}
                      dataKey="id"
                      emptyMessage="No best tax lots found"
                      selection={selectedBestLots}
                      onSelectionChange={(e) => setSelectedBestLots(e.value)}
                      selectionMode="multiple"
                    >
                  <Column
                    selectionMode="multiple"
                    style={{ width: "3rem" }}
                  />
                  <Column
                    expander={allowExpansion}
                    style={{ width: "1rem" }}
                    className={isMobileView ? "mobile-row-expander" : ""}
                  />
                  <Column 
                    field="symbol" 
                    header="Ticker" 
                    body={(rowData) => rowData.symbol || rowData.tickerSymbol || rowData.instrument_id || 'N/A'}
                  />
                  {/* {!isMobileView && (
                    <Column field="type" header="Action" />
                  )} */}
                  {!isMobileView && (
                    <Column field="quantity" header="Quantity" body={(rowData) => rowData.quantity != null ? formatNumberWithCommas(rowData.quantity, 2) : "0.00"} />
                  )}
                  {!isMobileView && (
                    <Column 
                      field="currentPrice" 
                      header="Current Price" 
                      body={(rowData) => rowData.currentPrice != null ? formatPrice(rowData.currentPrice) : "$0.00"}
                    />
                  )}
                  {!isMobileView && (
                    <Column field="costBasis" header="Cost Basis" body={(rowData) => rowData.costBasis != null ? formatPrice(rowData.costBasis) : "$0.00"} />
                  )}
                  {!isMobileView && (
                    <Column field="dateSold" header="Purchase Date" body={(rowData) => rowData.dateSold || rowData.date || rowData.purchaseDate || 'N/A'} />
                  )}
                  <Column
                    field="gainLoss"
                    header="Potential Gain/Loss"
                    body={renderPotentialGainLoss}
                  />
                  <Column 
                    field="totalAmount" 
                    header="Liquidation Value" 
                    body={(rowData) => (
                      <span className="table-highlight-text">
                        {rowData.totalAmount != null && rowData.totalAmount !== 0 ? formatCurrencyValue(rowData.totalAmount) : '$0.00'}
                      </span>
                    )}
                    sortable
                  />
                  {!isMobileView && (
                    <Column 
                      field="capitalGainTax" 
                      header="Capital Gain Tax" 
                      body={renderCapitalGainTax}
                    />
                  )}
                  {/* <Column field="washSale" header="Disallowed?" body={(rowData) => (rowData.washSale ? 'Yes' : 'No')} /> */}
                </DataTable>
                <div className="show-more-container">
                  {(analysis.documentData.bestTaxLots || []).length > bestLotsVisibleCount ? (
                    <Button
                      label={`Show More (${(analysis.documentData.bestTaxLots || []).length - bestLotsVisibleCount} remaining)`}
                      icon="pi pi-chevron-down"
                      onClick={() => setBestLotsVisibleCount(prev => Math.min(prev + 10, (analysis.documentData.bestTaxLots || []).length))}
                      className="p-button-outlined p-button-secondary show-more-button"
                    />
                  ) : bestLotsVisibleCount > 10 ? (
                    <Button
                      label="Show Less"
                      icon="pi pi-chevron-up"
                      onClick={() => setBestLotsVisibleCount(10)}
                      className="p-button-outlined p-button-secondary show-more-button"
                    />
                  ) : null}
                </div>
                {renderTotalsRow(
                  analysis.documentData.bestTaxLots || [], 
                  "Best Tax Lots",
                  analysis.documentData.fifoTaxImpact,
                  analysis.documentData.totalTaxImpact
                )}
                {selectedBestLots.length > 0 && (
                  <div className="execute-button-container">
                    {username === "demo" ? (
                      <Button
                        label={`Execute (${selectedBestLots.length} lot${selectedBestLots.length > 1 ? 's' : ''} selected)`}
                        icon="pi pi-check-circle"
                        onClick={handleExecuteSelectedLots}
                        className="p-button-success calculate-button-primary execute-button"
                        iconPos="left"
                      />
                    ) : (
                      <Button
                        label="Coming Soon"
                        icon="pi pi-clock"
                        disabled
                        className="p-button-secondary execute-button-disabled"
                        iconPos="left"
                      />
                    )}
                  </div>
                )}
                </>
                )}
              </div>

              {/* Other Lots Section */}
              {analysis.documentData.otherLots && analysis.documentData.otherLots.length > 0 && (
                <div className="results-section">
                  <div className="table-header-controls">
                    <h4 className="section-header">
                      📋 Other Available Lots ({analysis.documentData.otherLots?.length || 0} lots)
                    </h4>
                    <Button
                      icon={otherLotsTableCollapsed ? "pi pi-chevron-down" : "pi pi-chevron-up"}
                      onClick={() => {
                        setOtherLotsTableCollapsed(!otherLotsTableCollapsed);
                        if (otherLotsTableCollapsed) {
                          setOtherLotsVisibleCount(10);
                        }
                      }}
                      className="p-button-text p-button-sm collapse-button"
                      label={otherLotsTableCollapsed ? "Expand" : "Collapse"}
                    />
                  </div>
                  <p className="section-description">
                    Remaining lots that were not selected for the optimized liquidation.
                  </p>
                  {!otherLotsTableCollapsed && (
                    <>
                      <DataTable 
                        value={(analysis.documentData.otherLots || []).slice(0, otherLotsVisibleCount)} 
                        responsiveLayout="stack"
                        expandedRows={resultsExpandedRows}
                        onRowToggle={(e) => setResultsExpandedRows(e.data)}
                        rowExpansionTemplate={resultsRowExpansionTemplate}
                        sortField="totalAmount"
                        sortOrder={-1}
                        defaultSortOrder={-1}
                        dataKey="id"
                        emptyMessage="No other lots available"
                        paginator={false}
                      >
                    <Column
                      expander={allowExpansion}
                      style={{ width: "1rem" }}
                      className={isMobileView ? "mobile-row-expander" : ""}
                    />
                    <Column 
                      field="symbol" 
                      header="Ticker" 
                      body={(rowData) => rowData.symbol || rowData.tickerSymbol || rowData.instrument_id || 'N/A'}
                    />
                    {/* {!isMobileView && (
                      <Column field="type" header="Action" />
                    )} */}
                    {!isMobileView && (
                      <Column field="quantity" header="Quantity" body={(rowData) => rowData.quantity != null ? formatNumberWithCommas(rowData.quantity, 2) : "0.00"} />
                    )}
                    {!isMobileView && (
                      <Column 
                        field="currentPrice" 
                        header="Current Price" 
                        body={(rowData) => rowData.currentPrice != null ? formatPrice(rowData.currentPrice) : "$0.00"}
                      />
                    )}
                    {!isMobileView && (
                      <Column field="costBasis" header="Cost Basis" body={(rowData) => rowData.costBasis != null ? formatPrice(rowData.costBasis) : "$0.00"} />
                    )}
                    {!isMobileView && (
                      <Column field="dateSold" header="Purchase Date" body={(rowData) => rowData.dateSold || rowData.date || rowData.purchaseDate || 'N/A'} />
                    )}
                    <Column
                      field="gainLoss"
                      header="Potential Gain/Loss"
                      body={renderPotentialGainLoss}
                    />
                    <Column 
                      field="totalAmount" 
                      header="Liquidation Value" 
                      body={(rowData) => (
                        <span className="table-highlight-text">
                          {rowData.totalAmount != null && rowData.totalAmount !== 0 ? formatCurrencyValue(rowData.totalAmount) : '$0.00'}
                        </span>
                      )}
                      sortable
                    />
                    {!isMobileView && (
                      <Column 
                        field="capitalGainTax" 
                        header="Capital Gain Tax" 
                        body={renderCapitalGainTax}
                      />
                    )}
                    {/* <Column field="washSale" header="Disallowed?" body={(rowData) => (rowData.washSale ? 'Yes' : 'No')} /> */}
                  </DataTable>
                  <div className="show-more-container">
                    {(analysis.documentData.otherLots || []).length > otherLotsVisibleCount ? (
                      <Button
                        label={`Show More (${(analysis.documentData.otherLots || []).length - otherLotsVisibleCount} remaining)`}
                        icon="pi pi-chevron-down"
                        onClick={() => setOtherLotsVisibleCount(prev => Math.min(prev + 10, (analysis.documentData.otherLots || []).length))}
                        className="p-button-outlined p-button-secondary show-more-button"
                      />
                    ) : otherLotsVisibleCount > 10 ? (
                      <Button
                        label="Show Less"
                        icon="pi pi-chevron-up"
                        onClick={() => setOtherLotsVisibleCount(10)}
                        className="p-button-outlined p-button-secondary show-more-button"
                      />
                    ) : null}
                  </div>
                  {renderTotalsRow(analysis.documentData.otherLots || [], "Other Lots")}
                  </>
                  )}
                </div>
              )}
            </>
          ) : (
            // Regular results without liquidation amount
            <>
          <div className="table-header-controls">
            <h3>Scenario Results</h3>
            <Button
              icon={resultsTableCollapsed ? "pi pi-chevron-down" : "pi pi-chevron-up"}
              onClick={() => {
                setResultsTableCollapsed(!resultsTableCollapsed);
                if (resultsTableCollapsed) {
                  setResultsVisibleCount(10);
                }
              }}
              className="p-button-text p-button-sm collapse-button"
              label={resultsTableCollapsed ? "Expand" : "Collapse"}
            />
          </div>
          {!resultsTableCollapsed && (
            <>
              <DataTable 
                value={(analysis.documentData.transactions || results).slice(0, resultsVisibleCount)} 
                responsiveLayout="stack"
                expandedRows={resultsExpandedRows}
                onRowToggle={(e) => setResultsExpandedRows(e.data)}
                rowExpansionTemplate={resultsRowExpansionTemplate}
              >
            <Column
              expander={allowExpansion}
              style={{ width: "1rem" }}
              className={isMobileView ? "mobile-row-expander" : ""}
            />
            <Column field="symbol" header="Ticker" />
            {/* {!isMobileView && (
              <Column field="type" header="Action" />
            )} */}
            {!isMobileView && (
              <Column field="quantity" header="Quantity" body={(rowData) => rowData.quantity != null ? formatNumberWithCommas(rowData.quantity, 2) : "0.00"} />
            )}
            {!isMobileView && (
              <Column 
                field="currentPrice" 
                header="Current Price" 
                body={(rowData) => rowData.currentPrice != null ? formatPrice(rowData.currentPrice) : "$0.00"}
              />
            )}
            {!isMobileView && (
              <Column field="costBasis" header="Cost Basis" body={(rowData) => rowData.costBasis != null ? formatPrice(rowData.costBasis) : "$0.00"} />
            )}
            {!isMobileView && (
              <Column field="dateSold" header="Date" />
            )}
            <Column
              field="gainLoss"
              header="Potential Gain/Loss"
              body={renderPotentialGainLoss}
            />
                {!isMobileView && (
                  <Column 
                    field="totalAmount" 
                    header="Liquidation Value" 
                    body={(rowData) => (
                      <span className="table-highlight-text">
                        {rowData.totalAmount ? formatCurrencyValue(rowData.totalAmount) : 'N/A'}
                      </span>
                    )} 
                  />
                )}
                {!isMobileView && (
                  <Column 
                    field="capitalGainTax" 
                    header="Capital Gain Tax" 
                    body={renderCapitalGainTax}
                  />
                )}
            {/* <Column field="washSale" header="Disallowed?" body={(rowData) => (rowData.washSale ? 'Yes' : 'No')} /> */}
          </DataTable>
          <div className="show-more-container">
            {(analysis.documentData.transactions || results).length > resultsVisibleCount ? (
              <Button
                label={`Show More (${(analysis.documentData.transactions || results).length - resultsVisibleCount} remaining)`}
                icon="pi pi-chevron-down"
                onClick={() => setResultsVisibleCount(prev => Math.min(prev + 10, (analysis.documentData.transactions || results).length))}
                className="p-button-outlined p-button-secondary show-more-button"
              />
            ) : resultsVisibleCount > 10 ? (
              <Button
                label="Show Less"
                icon="pi pi-chevron-up"
                onClick={() => setResultsVisibleCount(10)}
                className="p-button-outlined p-button-secondary show-more-button"
              />
            ) : null}
          </div>
          {renderTotalsRow(analysis.documentData.transactions || [], "All Transactions")}
            </>
          )}
            </>
          )}
          </div>
        </div>
      )}
      
      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <Button
          icon="pi pi-arrow-up"
          onClick={scrollToTop}
          className="scroll-to-top-button"
          tooltip="Scroll to Top"
          tooltipOptions={{ position: 'left' }}
          rounded
        />
      )}

      {/* Unlock modal for results paywall */}
      <DashboardUnlock
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        estimatedSavings={
          analysis?.documentData?.fifoTaxImpact != null && analysis?.documentData?.totalTaxImpact != null
            ? Math.max(0, analysis.documentData.fifoTaxImpact - analysis.documentData.totalTaxImpact)
            : 0
        }
        onSuccess={handleUnlockSuccess}
        username={username}
      />

      {/* Execute Confirmation Dialog */}
      <Dialog
        header="Confirm Execution"
        visible={showExecuteConfirm}
        onHide={cancelExecute}
        style={{ width: '500px' }}
        className="execute-confirm-dialog"
        footer={
          <div className="execute-confirm-footer">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={cancelExecute}
              className="p-button-text p-button-secondary"
            />
            <Button
              label="Execute"
              icon="pi pi-check"
              onClick={confirmExecute}
              className="p-button-success calculate-button-primary"
            />
          </div>
        }
      >
        <div className="execute-confirm-content">
          <p className="execute-confirm-question">
            Execute {selectedBestLots.length} selected lot{selectedBestLots.length > 1 ? 's' : ''}?
          </p>
          <div className="execute-confirm-summary">
            <div className="execute-summary-item">
              <span className="execute-summary-label">Total Liquidation Value:</span>
              <span className="execute-summary-value">{formatCurrencyValue(executeSummary.liquidationValue)}</span>
            </div>
            <div className="execute-summary-item">
              <span className="execute-summary-label">Total estimated tax impact:</span>
              <span className="execute-summary-value">{formatCurrencyValue(executeSummary.taxImpact)}</span>
            </div>
          </div>
        </div>
          </Dialog>
      
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

export default TaxScenarioCalculator;